import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link as RouterLink } from 'react-router-dom';
import Container from '@mui/material/Container';
import Card from '@mui/material/Card';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import Breadcrumbs from '@mui/material/Breadcrumbs';
import Link from '@mui/material/Link';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Stack from '@mui/material/Stack';
import Chip from '@mui/material/Chip';
import Tooltip from '@mui/material/Tooltip';
import LoadingButton from '@mui/lab/LoadingButton';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import DialogContentText from '@mui/material/DialogContentText';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { Icon } from '@iconify/react';
import dayjs from 'dayjs';

import SuspenseDiv from '../../../layouts/SuspenseDiv';
import CreateProfileHeader from '../create-new-profile/header';
import ProfilePicturePopup from '../create-new-profile/profile-picture-popup';
import ProfileActivitiesTab from '../edit-profile/profile-activities-tab';
import ProfileEventsTab from '../edit-profile/profile-events-tab';
import ProfileAuditTab from '../edit-profile/profile-audit-tab';

import { useProfileForm } from './hooks/useProfileForm';
import { PROFILE_MODES, isRetryablePictureInductionError, isSkillkoSourcedEdit } from './profileFormConfig';
import {
  PersonalDetailsSection,
  EmergencyContactSection,
  CertificatesSection,
  EmployeeDetailsSection,
  AccessSection,
} from './sections';
import { SkillkoEditConfirmDialog } from './components';
import { useAvailableActions } from '../../../hooks/useAvailableActions';
import { useSnackbar } from 'notistack';
import profileApi from '../../../app/api/profileApi';
import { useTranslations } from '../../../hooks/use-translations';
import {
  PROFILE_FORM_CARD_SX,
  PROFILE_FORM_ERROR_STICKY_SX,
  PROFILE_FORM_LOADING_BACKDROP_SX,
} from './profileFormStyles';

/**
 * Unified Profile Form Component
 * 
 * A single component that handles Create, Edit, Skillko, and SitePass profile modes
 * based on the `mode` prop and configuration.
 * 
 * @param {string} mode - One of PROFILE_MODES: 'create', 'edit', 'skillko', 'sitepass'
 */
const ProfileForm = ({ mode }) => {
  const [isPicturePopup, setPicturePopup] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [retryLoading, setRetryLoading] = useState(false);
  const [forceSkillkoDialogOpen, setForceSkillkoDialogOpen] = useState(false);
  const theme = useTheme();
  const isSmUp = useMediaQuery(theme.breakpoints.up('sm'));
  const { canPerformProfileAdminActions } = useAvailableActions();
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslations();

  const {
    // Config
    config,

    // State
    isLoading,
    isInitialLoading,
    profileData,
    initialProfileData,
    image,
    setImage,
    errors,
    metadata,
    skillkoUrl,
    resolvedSkillkoId,
    skillkoRefreshLoading,
    forceSkillkoUpdateLoading,
    formRef,

    // Certificate form state
    selectedCertificate,
    setSelectedCertificate,
    certificateValidFrom,
    setCertificateValidFrom,
    certificateExpiresAt,
    setCertificateExpiresAt,
    certificateErrors,
    certificateNumber,
    setCertificateNumber,

    // Edit mode specific state
    isActivating,
    deleteDialogOpen,
    setDeleteDialogOpen,

    // Skillko critical-field edit confirmation state
    pendingSkillkoSection,
    pendingSkillkoFields,
    confirmSkillkoEdit,
    cancelSkillkoEdit,

    // Permissions
    canDeleteProfile,
    canEditProfile,
    isDeletedProfile,

    // Params
    currentSiteId,
    siteName,
    profileId,
    
    // Auth & Account (for tabs)
    auth,
    account,

    // Handlers
    handleInputChange,
    checkFieldDisabled,
    handleBackClick,
    scrollToErrorField,
    handleRefreshFromSkillko,
    handleForceUpdateFromSkillko,

    // Save handlers
    handleSaveProfile,
    handleSaveDraft,
    handleSaveAccess,
    handleSaveCertificates,

    // Skillko critical-field aware save requests (wrap the personal/next-of-kin/employee/picture handlers)
    requestSavePersonalDetails,
    requestSaveNextOfKin,
    requestSaveEmployeeDetails,
    requestSaveProfilePicture,

    // Edit mode handlers
    handleDeleteProfile,
    handleActivateProfile,

    // Certificate handlers
    handleAddCertificate,
    handleRemoveCertificate,

    // Metadata handlers
    handleAddNewRole,
    handleAddNewCompany,
    handleAddNewCertificateType,

    // Edit status checks
    isPersonalDetailsEdited,
    isNextOfKinEdited,
    isEmployeeDetailsEdited,
    isAccessEdited,
    isCertificatesEdited,
    isPictureEdited,
  } = useProfileForm(mode);

  // Use config properties for conditional rendering
  const { showDeleteButton, showActivateButton, showSkillkoLink, showSkillkoRefresh, sectionSave } = config;
  const isViewOnly = !canEditProfile && !isDeletedProfile;
  const useViewLabels = isViewOnly || (mode === PROFILE_MODES.EDIT && isDeletedProfile);
  const pageTitleKey = useViewLabels ? 'profiles.form.pageTitleView' : config.pageTitle;
  const breadcrumbTitleKey = useViewLabels ? 'profiles.form.breadcrumbView' : config.breadcrumbTitle;

  const showSkillkoRefreshButton =
    showSkillkoRefresh &&
    canEditProfile &&
    resolvedSkillkoId &&
    ((mode === PROFILE_MODES.EDIT && profileData.source === 'Skillko') || mode === PROFILE_MODES.SKILLKO);

  const showForceSkillkoUpdateButton =
    mode === PROFILE_MODES.EDIT &&
    canPerformProfileAdminActions &&
    profileData.source === 'Skillko' &&
    resolvedSkillkoId &&
    !isDeletedProfile;

  // Whether this profile is Skillko-sourced and being edited (critical-field warning applies)
  const isSkillkoEdit = isSkillkoSourcedEdit(mode, profileData);

  // Human-readable labels for the Skillko critical-field confirmation dialog
  const skillkoFieldLabels = {
    firstName: t('profiles.form.fieldFirstName'),
    surname: t('profiles.form.fieldSurname'),
    dateOfBirth: t('profiles.form.fieldDateOfBirth'),
    phone: t('profiles.form.fieldPhone'),
    nextOfKin: t('profiles.form.fieldNextOfKin'),
    nextOfKinPhone: t('profiles.form.fieldNextOfKinPhone'),
    company: t('profiles.form.fieldCompany'),
    role: t('profiles.form.fieldRole'),
    image: t('profiles.form.fieldPicture'),
  };
  const pendingSkillkoFieldLabels = pendingSkillkoFields.map((field) => skillkoFieldLabels[field] || field);

  // Check if user is admin (for Events tab - Edit mode only)
  const getIsAdmin = () => {
    try {
      if (!auth?.user || !auth.user.access_token) return false;
      const token = auth.user.access_token;
      const payload = token.split('.')[1];
      if (!payload) return false;
      const decoded = JSON.parse(atob(payload));
      return decoded?.realm_access?.roles?.includes('admin') || false;
    } catch (error) {
      return false;
    }
  };
  const isAdmin = getIsAdmin();

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  // Handle retry induction (Bosca admin only)
  const handleRetryInduction = async () => {
    setRetryLoading(true);
    try {
      await profileApi.retryProfileInduction(auth.user.access_token, {
        profileId: profileId,
        siteId: currentSiteId,
        accountId: account.id
      });
      enqueueSnackbar(t('profiles.form.retryInductionSuccess'), { variant: 'success' });
    } catch (error) {
      console.error('Error retrying induction:', error);
      const errorMessage = error.response?.data || t('profiles.form.retryInductionFailed');
      enqueueSnackbar(errorMessage, { variant: 'error' });
    } finally {
      setRetryLoading(false);
    }
  };

  const renderSkillkoRefreshButton = ({ fullWidth = false } = {}) => {
    if (!showSkillkoRefreshButton) {
      return null;
    }

    return (
      <Tooltip title={t('profiles.form.refreshFromSkillkoTooltip')} arrow placement="top">
        <span>
          <LoadingButton
            variant="outlined"
            onClick={handleRefreshFromSkillko}
            loading={skillkoRefreshLoading}
            disabled={skillkoRefreshLoading}
            startIcon={!skillkoRefreshLoading ? <Icon icon="mdi:cloud-sync" width="20" height="20" /> : null}
            sx={{
              width: fullWidth ? { xs: '100%', sm: 'auto' } : undefined,
              height: 'fit-content',
              margin: 'auto 0',
              fontSize: { xs: '0.875rem', sm: '0.9375rem' },
            }}
            fullWidth={fullWidth}
          >
            {skillkoRefreshLoading
              ? t('profiles.form.refreshingFromSkillko')
              : t('profiles.form.refreshFromSkillko')}
          </LoadingButton>
        </span>
      </Tooltip>
    );
  };

  // Render header buttons based on config
  const renderHeaderButtons = () => {
    // Don't show any action buttons for deleted profiles
    if (isDeletedProfile) {
      return null;
    }
    
    if (sectionSave) {
      return (
        <>
          {showActivateButton && profileData.status === 'Draft' && (
            <Grid item xs={12} sm="auto">
              <Button
                variant="contained"
                color="primary"
                onClick={handleActivateProfile}
                disabled={isActivating}
                startIcon={isActivating ? <CircularProgress size={20} /> : null}
                sx={{ 
                  width: { xs: '100%', sm: 'auto' },
                  height: 'fit-content', 
                  margin: 'auto 0',
                  fontSize: { xs: '0.875rem', sm: '0.9375rem' }
                }}
              >
                {isActivating ? t('profiles.form.activating') : t('profiles.form.activateProfile')}
              </Button>
            </Grid>
          )}
          {showDeleteButton && canDeleteProfile && (
            <Grid item xs={12} sm="auto">
              <Tooltip title={t('profiles.form.deleteProfileTooltip')} arrow placement="top">
                <Button
                  variant="contained"
                  onClick={() => setDeleteDialogOpen(true)}
                  color="error"
                  sx={{ 
                    width: { xs: '100%', sm: 'auto' },
                    height: 'fit-content', 
                    margin: 'auto 0',
                    fontSize: { xs: '0.875rem', sm: '0.9375rem' }
                  }}
                >
                  {t('profiles.form.deleteProfile')}
                </Button>
              </Tooltip>
            </Grid>
          )}
          {/* Retry Induction - Only visible to Bosca admins */}
          {canPerformProfileAdminActions && (
            <Grid item xs={12} sm="auto">
              <Tooltip title={t('profiles.form.retryInductionTooltip')} arrow placement="top">
                <Button
                  variant="contained"
                  color="warning"
                  onClick={handleRetryInduction}
                  disabled={retryLoading}
                  startIcon={retryLoading ? <CircularProgress size={20} color="inherit" /> : <Icon icon="mdi:restart" width="20" height="20" />}
                  sx={{ 
                    width: { xs: '100%', sm: 'auto' },
                    height: 'fit-content', 
                    margin: 'auto 0',
                    fontSize: { xs: '0.875rem', sm: '0.9375rem' }
                  }}
                >
                  {retryLoading ? t('profiles.form.retrying') : t('profiles.form.retryInduction')}
                </Button>
              </Tooltip>
            </Grid>
          )}
          {showSkillkoRefreshButton && (
            <Grid item xs={12} sm="auto">
              {renderSkillkoRefreshButton({ fullWidth: true })}
            </Grid>
          )}
          {showForceSkillkoUpdateButton && (
            <Grid item xs={12} sm="auto">
              <Tooltip title={t('profiles.form.forceUpdateFromSkillkoTooltip')} arrow placement="top">
                <span>
                  <LoadingButton
                    variant="outlined"
                    color="warning"
                    onClick={() => setForceSkillkoDialogOpen(true)}
                    loading={forceSkillkoUpdateLoading}
                    disabled={forceSkillkoUpdateLoading}
                    startIcon={!forceSkillkoUpdateLoading ? <Icon icon="mdi:database-sync" width="20" height="20" /> : null}
                    sx={{
                      width: { xs: '100%', sm: 'auto' },
                      height: 'fit-content',
                      margin: 'auto 0',
                      fontSize: { xs: '0.875rem', sm: '0.9375rem' },
                    }}
                    fullWidth
                  >
                    {forceSkillkoUpdateLoading
                      ? t('profiles.form.forcingUpdateFromSkillko')
                      : t('profiles.form.forceUpdateFromSkillko')}
                  </LoadingButton>
                </span>
              </Tooltip>
            </Grid>
          )}
          {showSkillkoLink && skillkoUrl && (
            <Grid item xs={12} sm="auto">
              <Button
                variant="outlined"
                onClick={() => window.open(skillkoUrl, '_blank', 'noopener,noreferrer')}
                startIcon={<Icon icon="mdi:open-in-new" width="20" height="20" />}
                sx={{ 
                  height: 'fit-content', 
                  margin: 'auto 0',
                  fontSize: { xs: '0.875rem', sm: '0.9375rem' }
                }}
                fullWidth={true}
              >
                {t('profiles.actionsMenu.openInSkillko')}
              </Button>
            </Grid>
          )}
        </>
      );
    }

    if (!sectionSave && (showSkillkoRefreshButton || (showSkillkoLink && skillkoUrl))) {
      return (
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ width: '100%' }}>
          {showSkillkoRefreshButton && renderSkillkoRefreshButton({ fullWidth: true })}
          {showSkillkoLink && skillkoUrl && (
            <Button
              variant="outlined"
              onClick={() => window.open(skillkoUrl, '_blank', 'noopener,noreferrer')}
              startIcon={<Icon icon="mdi:open-in-new" width="20" height="20" />}
              sx={{
                height: 'fit-content',
                fontSize: { xs: '0.875rem', sm: '0.9375rem' },
              }}
              fullWidth={true}
            >
              {t('profiles.actionsMenu.openInSkillko')}
            </Button>
          )}
        </Stack>
      );
    }

    return null;
  };

  // Render error alerts
  const renderErrorAlerts = () => {
    if (Object.keys(errors).length > 0 && errors._action) {
      return (
        <Box sx={PROFILE_FORM_ERROR_STICKY_SX}>
          <Stack sx={{ width: '100%' }} spacing={2}>
            <Alert
              severity="error"
              sx={{
                fontSize: { xs: '0.875rem', sm: '1rem' },
                '& .MuiAlert-message': { width: '100%' }
              }}
            >
              {errors._action === 'activate' 
                ? t('profiles.form.unableActivate')
                : errors._action === 'draft'
                  ? t('profiles.form.unableSaveDraft')
                  : t('profiles.form.unableSaveChanges')}
              <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '0.875rem' }}>
                {Object.entries(errors)
                  .filter(([key]) => key !== '_action')
                  .map(([field, message]) => (
                    <li 
                      key={field} 
                      style={{ cursor: 'pointer' }} 
                      onClick={() => scrollToErrorField(field)}
                    >
                      {message}
                    </li>
                  ))}
              </ul>
            </Alert>
          </Stack>
        </Box>
      );
    }

    return null;
  };

  // Render induction error (sectionSave mode only, i.e., Edit mode)
  const renderInductionError = () => {
    if (config.sections.showInductionError && isRetryablePictureInductionError(profileData.inductionErrorMessage)) {
      return (
        <Grid container>
          <Grid item xs={12} marginBottom={3}>
            <Alert severity="error">
              <Typography variant="body2">
                <strong>{t('profiles.form.pictureProcessingErrorTitle')}</strong> {t('profiles.form.pictureProcessingErrorBody')}
              </Typography>
            </Alert>
          </Grid>
        </Grid>
      );
    }
    return null;
  };

  // Render failed rules alert (when configured)
  const renderFailedRulesAlert = () => {
    if (config.sections.failedRulesAlert && profileData.failedRules && profileData.failedRules.length > 0) {
      return (
        <Container sx={{ mb: 2 }}>
          <Alert severity="warning" sx={{ mt: 2 }}>
            <AlertTitle sx={{ fontWeight: 600 }}>⚠️ {t('profiles.form.notReadyForInduction')}</AlertTitle>
            <Typography variant="body2" sx={{ mb: 1.5 }}>
              {t('profiles.form.notReadyDescription')}
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>
              {t('profiles.card.failedRules')}:
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {profileData.failedRules.map((rule, idx) => (
                <Chip
                  key={idx}
                  label={rule}
                  size="small"
                  color="warning"
                  sx={{ fontSize: '0.75rem' }}
                />
              ))}
            </Box>
          </Alert>
        </Container>
      );
    }
    return null;
  };

  // Render Skillko critical-field info banner (Edit mode, Skillko-sourced profiles only)
  const renderSkillkoBanner = () => {
    if (!isSkillkoEdit) {
      return null;
    }

    return (
      <Container sx={{ mb: 2 }}>
        <Alert severity="info">
          <Typography variant="body2" sx={{ mb: 1 }}>
            {t('profiles.form.skillkoBannerText')}
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
            {skillkoUrl && (
              <Button
                size="small"
                variant="outlined"
                onClick={() => window.open(skillkoUrl, '_blank', 'noopener,noreferrer')}
                startIcon={<Icon icon="mdi:open-in-new" width="16" height="16" />}
              >
                {t('profiles.actionsMenu.openInSkillko')}
              </Button>
            )}
            <LoadingButton
              size="small"
              variant="outlined"
              onClick={handleRefreshFromSkillko}
              loading={skillkoRefreshLoading}
              disabled={skillkoRefreshLoading}
              startIcon={!skillkoRefreshLoading ? <Icon icon="mdi:cloud-sync" width="16" height="16" /> : null}
            >
              {skillkoRefreshLoading ? t('profiles.form.refreshingFromSkillko') : t('profiles.form.refreshFromSkillko')}
            </LoadingButton>
          </Stack>
        </Alert>
      </Container>
    );
  };

  // Render induction expiry warning (SitePass mode)
  const renderInductionExpiryWarning = () => {
    if (config.sections.showInductionExpiryWarning && 
        profileData.inductionExpiry && 
        dayjs(profileData.inductionExpiry).isBefore(dayjs())) {
      return (
        <Container sx={{ mb: 2 }}>
          <Alert severity="warning">
            <AlertTitle>{t('profiles.form.inductionExpiryPassed')}</AlertTitle>
            {`${t('profiles.form.inductionExpiredOn')} ${dayjs(profileData.inductionExpiry).format('DD/MM/YYYY')}. ${t('profiles.form.verifyBeforeProceeding')}`}
          </Alert>
        </Container>
      );
    }
    return null;
  };

  return (
    <div>
      <Helmet>
        <title>{t(pageTitleKey)}</title>
      </Helmet>

      {isInitialLoading ? (
        <SuspenseDiv />
      ) : (
        <Container sx={{ px: { xs: 1, sm: 3 } }}>
          {/* Header with breadcrumbs and action buttons */}
          <Grid container spacing={isSmUp ? 0 : 1}>
            <Grid item xs={12} md={8}>
              <Box
                marginBottom="1em"
                marginLeft={{ xs: 0, sm: -2 }}
                sx={{ display: 'flex', alignItems: 'center' }}
              >
                <IconButton
                  onClick={handleBackClick}
                  sx={{
                    padding: 0,
                    marginBottom: '-13px',
                    flexShrink: 0,
                    '&:hover': { backgroundColor: 'transparent' }
                  }}
                >
                  <Icon
                    icon="mdi:chevron-left"
                    width="40"
                    height="40"
                    color={theme.palette.text.secondary}
                    style={{ cursor: 'pointer' }}
                  />
                </IconButton>
                <Breadcrumbs
                  data-testid="breadcrumb-navigation"
                  aria-label="breadcrumb"
                  sx={{
                    display: 'inline-block',
                    fontSize: { xs: '14px', sm: '20px' },
                    whiteSpace: 'nowrap'
                  }}
                >
                  <Link component={RouterLink} underline="hover" color="inherit" to="/">
                    {t('navigation.dashboard')}
                  </Link>
                  <Link
                    component={RouterLink}
                    underline="hover"
                    color="inherit"
                    to={`/profiles/${currentSiteId}`}
                    state={config.navigation.backState}
                  >
                    {t('profiles.sitesProfiles')}
                  </Link>
                  <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
                    <Typography
                      component="span"
                      sx={{
                        color: 'text.primary',
                        fontSize: { xs: '14px', sm: '20px' },
                        fontWeight: 700
                      }}
                    >
                      {t(breadcrumbTitleKey)}
                    </Typography>
                    {isDeletedProfile && (
                      <Chip label={t('profiles.tabs.deleted')} color="error" size="small" />
                    )}
                    {config.showSitePassChip && (
                      <Chip label={t('profiles.tabs.sitepassShort')} color="primary" size="small" />
                    )}
                  </Box>
                </Breadcrumbs>
                {siteName && (
                  <Box sx={{ display: 'inline-flex', alignItems: 'center', ml: { xs: 0, sm: 2 }, verticalAlign: 'middle' }}>
                    <Chip
                      icon={<Icon icon="mdi:map-marker" width="18" height="18" />}
                      label={`${t('profiles.siteLabel')}: ${siteName}`}
                      color="primary"
                      variant="outlined"
                      size="small"
                      sx={{
                        fontWeight: 600,
                        fontSize: '0.875rem',
                        height: '32px',
                      }}
                    />
                  </Box>
                )}
              </Box>
            </Grid>
            <Grid
              item
              container
              xs={12}
              md={4}
              alignItems="flex-end"
              sx={{ mb: { xs: 2, sm: sectionSave ? 0 : 1 } }}
            >
              {renderHeaderButtons()}
            </Grid>
          </Grid>

          {/* Main card */}
          <Card sx={PROFILE_FORM_CARD_SX}>
            {/* Loading overlay */}
            {isLoading && (
              <Backdrop
                sx={PROFILE_FORM_LOADING_BACKDROP_SX}
                open={isLoading}
              >
                <CircularProgress />
              </Backdrop>
            )}

            {/* Tabs for Edit mode only */}
            {sectionSave ? (
              <>
                <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                  <Tabs value={tabValue} onChange={handleTabChange} aria-label="profile tabs">
                    <Tab label={t(canEditProfile ? 'profiles.form.tabs.editProfile' : 'profiles.form.tabs.viewProfile')} />
                    <Tab label={t('profiles.form.tabs.profileActivities')} />
                    {isAdmin && <Tab label={t('navigation.events')} />}
                    {canEditProfile && <Tab label={t('profiles.form.tabs.changeHistory')} />}
                  </Tabs>
                </Box>

                {/* Edit Profile Tab */}
                <Box
                  role="tabpanel"
                  hidden={tabValue !== 0}
                  sx={{
                    paddingTop: { xs: 2, sm: 5 },
                    paddingLeft: { xs: 1, sm: 2 },
                    paddingBottom: { xs: 2, sm: 5 },
                    paddingRight: { xs: 1, sm: 2 },
                  }}
                >
                  {tabValue === 0 && (
                    <>
                      {isDeletedProfile && (
                        <Container sx={{ mb: 2 }}>
                          <Alert severity="warning" sx={{ mt: 2 }}>
                            <AlertTitle sx={{ fontWeight: 600 }}>{`⚠️ ${t('profiles.card.deletedProfile')}`}</AlertTitle>
                            <Typography variant="body2">
                              {t('profiles.form.deletedReadOnly')}
                            </Typography>
                          </Alert>
                        </Container>
                      )}
                      {isViewOnly && (
                        <Container sx={{ mb: 2 }}>
                          <Alert severity="info" sx={{ mt: 2 }}>
                            <Typography variant="body2">
                              {t('profiles.form.viewOnlyCannotRegister')}
                            </Typography>
                          </Alert>
                        </Container>
                      )}
                      <Container>
                        <CreateProfileHeader
                          profileData={profileData}
                          imageSrc={image}
                          openPicturePopup={() => setPicturePopup(true)}
                          isEditProfile={true}
                          newProfileText={t(config.newProfileText)}
                          error={errors.image}
                          isDeletedProfile={isDeletedProfile}
                          onSavePicture={requestSaveProfilePicture}
                          isPictureEdited={isPictureEdited()}
                          canEdit={canEditProfile}
                          isSkillkoManaged={isSkillkoEdit}
                        />
                      </Container>

                      {renderFailedRulesAlert()}
                      {renderSkillkoBanner()}

                      <Container>
                        <Box sx={{ p: 0 }} ref={formRef}>
                          {renderErrorAlerts()}
                          {renderInductionError()}

                          <Grid container>
                            <PersonalDetailsSection
                              mode={mode}
                              config={config}
                              profileData={profileData}
                              initialProfileData={initialProfileData}
                              errors={errors}
                              handleInputChange={handleInputChange}
                              checkFieldDisabled={checkFieldDisabled}
                              showEmail={config.sections.showEmail}
                              onSave={requestSavePersonalDetails}
                              isEdited={isPersonalDetailsEdited()}
                              canEdit={canEditProfile}
                            />

                            <EmergencyContactSection
                              mode={mode}
                              config={config}
                              profileData={profileData}
                              initialProfileData={initialProfileData}
                              handleInputChange={handleInputChange}
                              checkFieldDisabled={checkFieldDisabled}
                              onSave={requestSaveNextOfKin}
                              isEdited={isNextOfKinEdited()}
                              canEdit={canEditProfile}
                            />

                            <CertificatesSection
                              config={config}
                              profileData={profileData}
                              metadata={metadata}
                              errors={errors}
                              selectedCertificate={selectedCertificate}
                              setSelectedCertificate={setSelectedCertificate}
                              certificateValidFrom={certificateValidFrom}
                              setCertificateValidFrom={setCertificateValidFrom}
                              certificateExpiresAt={certificateExpiresAt}
                              setCertificateExpiresAt={setCertificateExpiresAt}
                              certificateErrors={certificateErrors}
                              certificateNumber={certificateNumber}
                              setCertificateNumber={setCertificateNumber}
                              handleAddCertificate={handleAddCertificate}
                              handleRemoveCertificate={handleRemoveCertificate}
                              handleAddNewCertificateType={handleAddNewCertificateType}
                              onSave={handleSaveCertificates}
                              isEdited={isCertificatesEdited()}
                              canEdit={canEditProfile}
                            />

                            <EmployeeDetailsSection
                              mode={mode}
                              config={config}
                              profileData={profileData}
                              initialProfileData={initialProfileData}
                              metadata={metadata}
                              errors={errors}
                              handleInputChange={handleInputChange}
                              checkFieldDisabled={checkFieldDisabled}
                              handleAddNewCompany={handleAddNewCompany}
                              handleAddNewRole={handleAddNewRole}
                              onSave={requestSaveEmployeeDetails}
                              isEdited={isEmployeeDetailsEdited()}
                              canEdit={canEditProfile}
                            />

                            <AccessSection
                              mode={mode}
                              config={config}
                              profileData={profileData}
                              initialProfileData={initialProfileData}
                              metadata={metadata}
                              errors={errors}
                              handleInputChange={handleInputChange}
                              checkFieldDisabled={checkFieldDisabled}
                              onSave={handleSaveAccess}
                              onDraftSave={handleSaveDraft}
                              isEdited={isAccessEdited()}
                              canEdit={canEditProfile}
                            />
                          </Grid>
                        </Box>
                      </Container>
                    </>
                  )}
                </Box>

                {/* Profile Activities Tab */}
                <Box role="tabpanel" hidden={tabValue !== 1}>
                  {tabValue === 1 && (
                    <ProfileActivitiesTab
                      auth={auth}
                      siteId={currentSiteId}
                      accountId={account?.id}
                      profileId={profileId}
                    />
                  )}
                </Box>

                {/* Events Tab (Admin only) */}
                {isAdmin && (
                  <Box role="tabpanel" hidden={tabValue !== 2}>
                    {tabValue === 2 && (
                      <ProfileEventsTab
                        auth={auth}
                        siteId={currentSiteId}
                        accountId={account?.id}
                        profileId={profileId}
                      />
                    )}
                  </Box>
                )}

                {/* Change History Tab */}
                {canEditProfile && (
                  <Box role="tabpanel" hidden={tabValue !== (isAdmin ? 3 : 2)}>
                    {tabValue === (isAdmin ? 3 : 2) && (
                      <ProfileAuditTab
                        auth={auth}
                        siteId={currentSiteId}
                        accountId={account?.id}
                        profileId={profileId}
                      />
                    )}
                  </Box>
                )}
              </>
            ) : (
              /* Create/Skillko/SitePass mode - no tabs */
              <Box
                sx={{
                  paddingTop: { xs: 2, sm: 5 },
                  paddingBottom: { xs: 2, sm: 5 },
                }}
              >
                {isDeletedProfile && (
                  <Container sx={{ mb: 2 }}>
                    <Alert severity="warning" sx={{ mt: 2 }}>
                      <AlertTitle sx={{ fontWeight: 600 }}>{`⚠️ ${t('profiles.card.deletedProfile')}`}</AlertTitle>
                      <Typography variant="body2">
                        {t('profiles.form.deletedReadOnly')}
                      </Typography>
                    </Alert>
                  </Container>
                )}
                {!canEditProfile && !isDeletedProfile && (
                  <Container sx={{ mb: 2 }}>
                    <Alert severity="info" sx={{ mt: 2 }}>
                      <Typography variant="body2">
                        {t('profiles.form.viewOnlyCannotRegister')}
                      </Typography>
                    </Alert>
                  </Container>
                )}
                <CreateProfileHeader
                  profileData={profileData}
                  imageSrc={image}
                  openPicturePopup={() => setPicturePopup(true)}
                  isEditProfile={false}
                  newProfileText={t(config.newProfileText)}
                  error={errors.image}
                  isDeletedProfile={isDeletedProfile}
                  canEdit={canEditProfile}
                />

                {renderInductionExpiryWarning()}
                {renderFailedRulesAlert()}

                <Container>
                  <Box sx={{ p: { xs: 1, sm: 3 } }} ref={formRef}>
                    {renderErrorAlerts()}
                    {renderInductionError()}

                    <Grid container>
                      <PersonalDetailsSection
                        mode={mode}
                        config={config}
                        profileData={profileData}
                        initialProfileData={initialProfileData}
                        errors={errors}
                        handleInputChange={handleInputChange}
                        checkFieldDisabled={checkFieldDisabled}
                        showEmail={config.sections.showEmail}
                        onSave={requestSavePersonalDetails}
                        isEdited={isPersonalDetailsEdited()}
                        canEdit={canEditProfile}
                      />

                      <EmergencyContactSection
                        mode={mode}
                        config={config}
                        profileData={profileData}
                        initialProfileData={initialProfileData}
                        handleInputChange={handleInputChange}
                        checkFieldDisabled={checkFieldDisabled}
                        onSave={requestSaveNextOfKin}
                        isEdited={isNextOfKinEdited()}
                        canEdit={canEditProfile}
                      />

                      <CertificatesSection
                        config={config}
                        profileData={profileData}
                        metadata={metadata}
                        errors={errors}
                        selectedCertificate={selectedCertificate}
                        setSelectedCertificate={setSelectedCertificate}
                        certificateValidFrom={certificateValidFrom}
                        setCertificateValidFrom={setCertificateValidFrom}
                        certificateExpiresAt={certificateExpiresAt}
                        setCertificateExpiresAt={setCertificateExpiresAt}
                        certificateErrors={certificateErrors}
                        certificateNumber={certificateNumber}
                        setCertificateNumber={setCertificateNumber}
                        handleAddCertificate={handleAddCertificate}
                        handleRemoveCertificate={handleRemoveCertificate}
                        handleAddNewCertificateType={handleAddNewCertificateType}
                        onSave={handleSaveCertificates}
                        isEdited={isCertificatesEdited()}
                        canEdit={canEditProfile}
                      />

                      <EmployeeDetailsSection
                        mode={mode}
                        config={config}
                        profileData={profileData}
                        initialProfileData={initialProfileData}
                        metadata={metadata}
                        errors={errors}
                        handleInputChange={handleInputChange}
                        checkFieldDisabled={checkFieldDisabled}
                        handleAddNewCompany={handleAddNewCompany}
                        handleAddNewRole={handleAddNewRole}
                        onSave={requestSaveEmployeeDetails}
                        isEdited={isEmployeeDetailsEdited()}
                        canEdit={canEditProfile}
                      />

                      <AccessSection
                        mode={mode}
                        config={config}
                        profileData={profileData}
                        initialProfileData={initialProfileData}
                        metadata={metadata}
                        errors={errors}
                        handleInputChange={handleInputChange}
                        checkFieldDisabled={checkFieldDisabled}
                        onSave={handleSaveProfile}
                        onDraftSave={handleSaveDraft}
                        isEdited={isAccessEdited()}
                        canEdit={canEditProfile}
                      />
                    </Grid>
                  </Box>
                </Container>
              </Box>
            )}
          </Card>
        </Container>
      )}

      {/* Profile Picture Popup */}
      {isPicturePopup && (
        <ProfilePicturePopup
          setImage={setImage}
          isEditProfile={sectionSave}
          onClose={() => setPicturePopup(false)}
        />
      )}

      {/* Delete Confirmation Dialog (when delete button is configured) */}
      {showDeleteButton && (
        <Dialog
          open={deleteDialogOpen}
          onClose={() => setDeleteDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>{t('profiles.form.confirmDeleteTitle')}</DialogTitle>
          <DialogContent>
            <DialogContentText>
              {`${t('profiles.form.confirmDeleteDescriptionPrefix')} `}
              <strong>{profileData.firstName} {profileData.surname}</strong>
              {`? ${t('profiles.form.confirmDeleteDescriptionSuffix')}`}
            </DialogContentText>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={() => setDeleteDialogOpen(false)} color="primary" variant="outlined">
              {t('common.cancel')}
            </Button>
            <Button onClick={handleDeleteProfile} color="error" variant="contained" autoFocus>
              {t('profiles.form.delete')}
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {showForceSkillkoUpdateButton && (
        <Dialog
          open={forceSkillkoDialogOpen}
          onClose={() => {
            if (!forceSkillkoUpdateLoading) setForceSkillkoDialogOpen(false);
          }}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>{t('profiles.form.forceUpdateFromSkillkoConfirmTitle')}</DialogTitle>
          <DialogContent>
            <DialogContentText>
              {t('profiles.form.forceUpdateFromSkillkoConfirmBody')}
            </DialogContentText>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button
              onClick={() => setForceSkillkoDialogOpen(false)}
              color="primary"
              variant="outlined"
              disabled={forceSkillkoUpdateLoading}
            >
              {t('common.cancel')}
            </Button>
            <LoadingButton
              onClick={async () => {
                await handleForceUpdateFromSkillko();
                setForceSkillkoDialogOpen(false);
              }}
              color="warning"
              variant="contained"
              loading={forceSkillkoUpdateLoading}
            >
              {t('profiles.form.forceUpdateFromSkillkoConfirm')}
            </LoadingButton>
          </DialogActions>
        </Dialog>
      )}

      {/* Skillko Critical Field Edit Confirmation Dialog */}
      <SkillkoEditConfirmDialog
        open={!!pendingSkillkoSection}
        changedFieldLabels={pendingSkillkoFieldLabels}
        skillkoUrl={skillkoUrl}
        refreshLoading={skillkoRefreshLoading}
        onCancel={cancelSkillkoEdit}
        onOpenSkillko={() => window.open(skillkoUrl, '_blank', 'noopener,noreferrer')}
        onRefresh={async () => {
          await handleRefreshFromSkillko();
          cancelSkillkoEdit();
        }}
        onConfirm={confirmSkillkoEdit}
      />
    </div>
  );
};

// Export modes for convenience
export { PROFILE_MODES };

export default ProfileForm;

